/**
 * «Варианты» выделенного предмета: все модели его типа из каталога — серая, тауп
 * и дубовая двуспальные кровати. Каталог показывает тип одной карточкой, а модель
 * выбирают здесь, у поставленного предмета: клик — предмет меняет модель на том же
 * месте (`replaceSelection`), спинка остаётся у стены. Текущая модель подсвечена.
 *
 * Полоса одна для каталога чертежа и 3D-каталога. У предмета без вариантов её нет.
 */
import { useState } from "react";
import { familyName, familyVariants, type CatalogItem, type View3D } from "@/shared/catalog";
import { ModelPeek, ModelPreview, PreviewEyeButton, useModelPeek } from "@/widgets/model-preview";
import { findFixture, findFloorItem, type CatalogRef, type PlanLayers } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { CatalogSymbol } from "@/widgets/catalog";
import { SelectionKind, useEditorStore, type Selection } from "@/widgets/editor";
import { replaceSelection } from "../logic";

/**
 * Название варианта без общей с типом части: «Кровать двуспальная, тауп» → «Тауп»,
 * «Кровать деревянная, дуб» → «Деревянная, дуб». Целиком в карточке не помещается,
 * а «Кровать двуспальна…» у всех одинаковое.
 */
const variantLabel = (name: string, family: string): string => {
	const familyWords = family.toLowerCase().split(/\s+/);
	const words = name.split(/\s+/);
	let common = 0;
	while (
		common < words.length - 1 &&
		common < familyWords.length &&
		words[common].toLowerCase().replace(/[,]/g, "") === familyWords[common]
	) {
		common += 1;
	}
	const rest = words
		.slice(common)
		.join(" ")
		.replace(/^[,\s-]+/, "");
	return rest ? rest[0].toUpperCase() + rest.slice(1) : name;
};

/** Ссылка на каталог у выделенного объекта: на полу, на стене или на потолке. */
const selectedRef = (selection: Selection | null, layers: PlanLayers): CatalogRef | null => {
	if (!selection) return null;
	if (selection.kind === SelectionKind.Item)
		return findFloorItem(layers, selection.id)?.item.ref ?? null;
	if (selection.kind === SelectionKind.Fixture)
		return findFixture(layers, selection.id)?.fixture.ref ?? null;
	if (selection.kind === SelectionKind.Light)
		return layers.electrical.items[selection.id]?.ref ?? null;
	return null;
};

interface Props {
	/** В панели свойств вариант выбирают у уже поставленного предмета, без каталога. */
	location?: "catalog" | "properties";
}

export const VariantsStrip = ({ location = "catalog" }: Props) => {
	const selection = useEditorStore((s) => s.selection);
	const activeCatalog = useEditorStore((s) => s.activeCatalog);
	const setSelection = useEditorStore((s) => s.setSelection);
	const layers = useProjectStore((s) => s.project.layers);
	const [preview, setPreview] = useState<View3D | null>(null);
	// Навёл на вариант — рядом его модель в 3D: какая кровать какая, видно без клика
	const { peek, peekProps, closePeek } = useModelPeek();
	const ref = selectedRef(selection, layers);
	if (location === "catalog" && ref && ref.catalog !== activeCatalog) return null;
	const variants = ref ? familyVariants(ref) : [];
	if (!ref || variants.length === 0) return null;
	const family = familyName(ref) ?? "";

	const pick = (item: CatalogItem) => {
		if (item.id === ref.item) return;
		const replaced = replaceSelection(selection, { catalog: ref.catalog, item: item.id });
		if (replaced) setSelection(replaced);
	};

	return (
		<div className="shrink-0 rounded-lg border border-accent/25 bg-accent-soft/60 p-1.5">
			<UIKit.Text className="mb-1 px-0.5 text-xs font-semibold text-accent">
				Варианты: {family}
			</UIKit.Text>
			<div className="grid grid-cols-3 gap-1">
				{variants.map((item) => (
					<VariantCard
						key={item.id}
						item={item}
						label={variantLabel(item.name, family)}
						active={item.id === ref.item}
						onClick={() => pick(item)}
						onPreview={() => {
							closePeek();
							setPreview(item.view3d);
						}}
						peekProps={peekProps(item, item.name)}
					/>
				))}
			</div>
			{preview && <ModelPreview view3d={preview} onClose={() => setPreview(null)} />}
			{peek && <ModelPeek peek={peek} />}
		</div>
	);
};

/** Маленькая карточка модели: символ и название. */
function VariantCard({
	item,
	label,
	active,
	onClick,
	onPreview,
	peekProps,
}: {
	item: CatalogItem;
	label: string;
	active: boolean;
	onClick: () => void;
	onPreview: () => void;
	/** Просмотр модели по наведению — см. useModelPeek */
	peekProps: ReturnType<ReturnType<typeof useModelPeek>["peekProps"]>;
}) {
	const w = item.width;
	const d = item.depth || item.height;
	const pad = Math.max(w, d) * 0.15;
	return (
		<div className="group relative" {...peekProps}>
			<button
				type="button"
				title={item.description}
				aria-pressed={active}
				className={`flex w-full flex-col items-center rounded-md border bg-white p-1 transition-colors${
					active ? " border-accent" : " border-transparent hover:border-[#c0cce0]"
				}`}
				onClick={onClick}
			>
				<svg
					viewBox={`${-w / 2 - pad} ${-d / 2 - pad} ${w + pad * 2} ${d + pad * 2}`}
					className="h-8 w-full"
					preserveAspectRatio="xMidYMid meet"
				>
					<CatalogSymbol
						item={item}
						width={w}
						height={d}
						color={active ? "#2f6feb" : "#475569"}
						mmPerPx={Math.max(w, d) / 60}
					/>
				</svg>
				<UIKit.Text
					as="span"
					className="line-clamp-2 w-full text-center text-xs leading-tight text-ink"
				>
					{label}
				</UIKit.Text>
			</button>
			<PreviewEyeButton onClick={onPreview} />
		</div>
	);
}
